import React, { useState } from 'react';
import {
  Box,
  Chip,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import Iconify from '../../../components/iconify';
import { QUICK_FILTERS } from './pipelineConfig';

const QuickFilterBar = ({
  activeFilter,
  onFilterChange,
  savedFilters = [],
  onSaveFilter,
  onDeleteFilter,
}) => {
  const [savedMenuAnchor, setSavedMenuAnchor] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');

  const handleQuickFilter = (filterId) => {
    if (activeFilter === filterId) {
      onFilterChange(null); // toggle off
    } else {
      onFilterChange(filterId);
    }
  };

  const handleSaveFilter = () => {
    if (newFilterName.trim() && activeFilter) {
      onSaveFilter({ name: newFilterName.trim(), filters: { quickFilter: activeFilter } });
      setNewFilterName('');
      setSaveDialogOpen(false);
    }
  };

  const handleLoadSavedFilter = (filter) => {
    if (filter.filters?.quickFilter) {
      onFilterChange(filter.filters.quickFilter);
    }
    setSavedMenuAnchor(null);
  };

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
      {/* Quick filter chips */}
      {QUICK_FILTERS.map((filter) => (
        <Chip
          key={filter.id}
          label={filter.label}
          icon={<Iconify icon={filter.icon} width={16} />}
          onClick={() => handleQuickFilter(filter.id)}
          variant={activeFilter === filter.id ? 'filled' : 'outlined'}
          color={activeFilter === filter.id ? 'primary' : 'default'}
          size="small"
          sx={{
            fontWeight: activeFilter === filter.id ? 600 : 400,
            transition: 'all 0.2s ease',
          }}
        />
      ))}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Saved filters */}
      {savedFilters.length > 0 && (
        <>
          <Chip
            label="Saved Filters"
            icon={<BookmarkBorderIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setSavedMenuAnchor(e.currentTarget)}
            variant="outlined"
            size="small"
            color="secondary"
          />
          <Menu
            anchorEl={savedMenuAnchor}
            open={Boolean(savedMenuAnchor)}
            onClose={() => setSavedMenuAnchor(null)}
          >
            {savedFilters.map((filter) => (
              <MenuItem key={filter._id} sx={{ fontSize: '0.85rem' }}>
                <ListItemIcon>
                  <BookmarkIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText onClick={() => handleLoadSavedFilter(filter)}>
                  {filter.name}
                </ListItemText>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFilter(filter._id);
                  }}
                  sx={{ ml: 1 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Save current filter button */}
      {activeFilter && (
        <Chip
          label="Save Filter"
          icon={<SaveIcon sx={{ fontSize: 14 }} />}
          onClick={() => setSaveDialogOpen(true)}
          variant="outlined"
          size="small"
          sx={{ borderStyle: 'dashed' }}
        />
      )}

      {/* Clear filter */}
      {activeFilter && (
        <Chip
          label="Clear"
          onDelete={() => onFilterChange(null)}
          size="small"
          color="default"
        />
      )}

      {/* Save dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save Current Filter</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Filter Name"
            value={newFilterName}
            onChange={(e) => setNewFilterName(e.target.value)}
            margin="dense"
            placeholder="e.g. Hot New Leads"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSaveFilter} variant="contained" disabled={!newFilterName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickFilterBar;
